import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { StatsCard } from '../components/ui/StatsCard';
import { Card, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Table } from '../components/ui/Table';
import { useNavigate } from 'react-router-dom';
import { isAdmin, isIntegrator, isCustomer, isPropertyManager } from '../utils/roles';
import { getProperties } from '../api/properties';
import { getCustomers } from '../api/customers';
import { getDevices } from '../api/devices';
import { getUnits } from '../api/units';
import { getTenants } from '../api/tenants';
import { apiList, apiRequestGet } from '../api/apiClient';
import { calcDeviceCost } from '../config/billing';
import { Property, Customer } from '../types';
import { DashboardWorkspace } from '../components/dashboard/DashboardWorkspace';
import { normalizeDeviceConnectivity } from '../utils/deviceConnectivity';
import { computeMonthlyRevenue } from '../utils/revenue';
export { computeMonthlyRevenue } from '../utils/revenue';

interface DeviceStatus {
  locked?: boolean;
  doorOpen?: boolean;
  online?: boolean;
  alarm?: boolean;
  connection_status?: string;
  last_seen?: string;
  integration_error?: string;
}

const icon = (d: string) => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={d} /></svg>;
const bridgeIcon = icon('M13 10V3L4 14h7v7l9-11h-7z'); // lightning bolt for bridge
const ICONS = {
  integrators: icon('M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z'),
  customers: icon('M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5'),
  properties: icon('M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'),
  units: icon('M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1'),
  devices: icon('M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z'),
  tenants: icon('M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'),
  mobile: icon('M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z'),
};

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || 'customer';

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>({});
  const [propertyList, setPropertyList] = useState<Property[]>([]);
  const [customerList, setCustomerList] = useState<Customer[]>([]);
  const [deviceStatuses, setDeviceStatuses] = useState<Record<string, DeviceStatus>>({});
  const [statusLoading, setStatusLoading] = useState(false);
  const [allFilteredDevices, setAllFilteredDevices] = useState<any[]>([]);
  const [activityItems, setActivityItems] = useState<any[]>([]);

  const fetchDeviceStatuses = async (devArr: any[], propArr: any[]) => {
    setStatusLoading(true);
    const statuses: Record<string, DeviceStatus> = {};
    try {
      const propertyIds = Array.from(new Set(propArr
        .map((property: any) => String(property.uuid || property.id || ''))
        .filter(Boolean)));
      const results = await Promise.all(propertyIds.map(async (propertyId) => {
        try {
          const rows = await apiRequestGet(`/devices/live?property_id=${encodeURIComponent(propertyId)}`);
          return Array.isArray(rows) ? rows : [];
        } catch (error) {
          console.error(`[Dashboard] Failed to fetch property-scoped device status for ${propertyId}:`, error);
          return [];
        }
      }));
      for (const live of results.flat()) {
        const id = String(live.id || live.uuid || '');
        if (!id) continue;
        statuses[id] = {
          locked: live.locked,
          doorOpen: live.door_open ?? live.dps_open,
          online: typeof live.online === 'boolean' ? live.online : undefined,
          alarm: live.alarm,
          connection_status: live.connection_status,
          last_seen: live.last_seen,
          integration_error: live.integration_error,
        };
      }
      setDeviceStatuses(statuses);
    } finally {
      setStatusLoading(false);
    }
  };

  const getDeviceOnline = (d: any) => {
    return normalizeDeviceConnectivity(d, deviceStatuses[String(d.uuid || d.id)]).state === 'online';
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      console.time('dashboard-init');
      try {
        // Fire all independent fetches in parallel — each Lambda is a separate
        // function so concurrency here doesn't saturate a single cold container.
        // We use ?limit=100 on the heavy lists since Dashboard only shows counts
        // and a 5-row preview, not the full population.
        const [
          properties,
          devices,
          customers,
          integrators,
          tenants,
          units,
          callHistory,
          videoCalls,
          unlockEvents,
          auditEvents,
        ] = await Promise.all([
          getProperties().catch(() => [] as any[]),
          getDevices().catch(() => [] as any[]),
          (isAdmin(role) || isIntegrator(role)) ? getCustomers().catch(() => [] as any[]) : Promise.resolve([] as any[]),
          isAdmin(role) ? apiList('integrators').catch(() => [] as any[]) : Promise.resolve([] as any[]),
          getTenants().catch(() => [] as any[]),
          getUnits().catch(() => [] as any[]),
          apiList('call_history').catch(() => [] as any[]),
          apiList('video_calls').catch(() => [] as any[]),
          fetch(`${process.env.REACT_APP_API_URL || 'https://api.intrlock.io'}/acs-systems/unlock-events?limit=500`, { headers: { Authorization: `Bearer ${localStorage.getItem('intrlock_token') || ''}` } }).then(response => response.ok ? response.json() : { items: [] }).then(data => data.items || []).catch(() => [] as any[]),
          apiList('audit_events').catch(() => [] as any[]),
        ]);
        // bridge devices remain stubbed (per-bridge fetch on PropertyDetail)
        const bridgeDevices: any[] = [];
        if (cancelled) return;

        let filteredProps = properties;
        let filteredCustomers = customers;
        let filteredDevices = devices;
        let filteredTenants = tenants;
        let filteredUnits = units;

        // Helper: build a Set of property UUIDs for filtering child records
        const makePropIdSet = (props: any[]) => new Set(props.map((p: any) => p.uuid || String(p.id)));

        if (isIntegrator(role) && user?.integrator_id) {
          const myCustomerIds = new Set(customers.filter((c: any) => String(c.integrator_id) === String(user.integrator_id) || String(c.dealer_id) === String(user.integrator_id)).map((c: any) => c.uuid || String(c.id)));
          filteredCustomers = customers.filter((c: any) => myCustomerIds.has(c.uuid || String(c.id)));
          filteredProps = properties.filter((p: any) => myCustomerIds.has(p.customer_id));
          const propIds = makePropIdSet(filteredProps);
          filteredDevices = devices.filter((d: any) => propIds.has(d.property_id));
          filteredTenants = tenants.filter((t: any) => propIds.has(t.property_id));
          filteredUnits = units.filter((u: any) => propIds.has(u.property_id));
        } else if (isCustomer(role) && user?.customer_id) {
          filteredProps = properties.filter((p: any) => String(p.customer_id) === String(user.customer_id));
          const propIds = makePropIdSet(filteredProps);
          filteredDevices = devices.filter((d: any) => propIds.has(d.property_id));
          filteredTenants = tenants.filter((t: any) => propIds.has(t.property_id));
          filteredUnits = units.filter((u: any) => propIds.has(u.property_id));
        } else if (isPropertyManager(role) && user?.property_ids?.length) {
          const myPropIds = new Set(user.property_ids.map(String));
          filteredProps = properties.filter((p: any) => myPropIds.has(p.uuid || String(p.id)));
          filteredDevices = devices.filter((d: any) => myPropIds.has(d.property_id));
          filteredTenants = tenants.filter((t: any) => myPropIds.has(t.property_id));
          filteredUnits = units.filter((u: any) => myPropIds.has(u.property_id));
        } else if (role === 'viewer' && user?.property_ids?.length) {
          const viewerPropIds = new Set(user.property_ids.map(String));
          filteredProps = properties.filter((p: any) => viewerPropIds.has(p.uuid || String(p.id)));
          filteredDevices = devices.filter((d: any) => viewerPropIds.has(d.property_id));
          filteredTenants = tenants.filter((t: any) => viewerPropIds.has(t.property_id));
          filteredUnits = units.filter((u: any) => viewerPropIds.has(u.property_id));
        } else if (role === 'tenant') {
          filteredProps = [];
          filteredDevices = [];
          filteredTenants = [];
          filteredUnits = [];
          filteredCustomers = [];
        }

        if (!cancelled) setAllFilteredDevices(filteredDevices);
        // Fetch live statuses in background (non-blocking)
        fetchDeviceStatuses(filteredDevices, filteredProps);

        const onlineDevices = filteredDevices.filter(getDeviceOnline).length;
        const phones = filteredProps.filter((p: any) => p.telnyx_number).length;
        // Use server-side total_monthly_revenue from counter refresh
        const monthlyAmount = computeMonthlyRevenue(filteredProps as any[]);

        // Separate bridge relay devices
        const propIds = makePropIdSet(filteredProps);
        const filteredBridgeChannels = bridgeDevices.filter((b: any) => propIds.has(b.property_id));
        const onlineBridgeChannels = filteredBridgeChannels.filter((b: any) => b.status === 'online').length;

        // Call/video counts per property
        const filteredCalls = callHistory.filter((c: any) => propIds.has(c.property_id));
        const filteredVideoCalls = videoCalls.filter((v: any) => propIds.has(v.property_id));

        // Enrich customers with server-side counters (fall back to client-side if not available)
        const enrichedCustomers = filteredCustomers.map((c: any) => ({
          ...c,
          _properties: c.total_properties || 0,
          _units: c.total_units || 0,
          _devices: c.total_devices || 0,
          _tenants: c.total_tenants || 0,
          _phones: c.total_phones || 0,
          _monthly: Number(c.total_monthly_revenue) || 0,
        }));

        // Enrich properties with server-side counters
        const enrichedProperties = filteredProps.map((p: any) => ({
          ...p,
          _units: p.total_units || 0,
          _devices: p.total_devices || 0,
          _tenants: p.total_tenants || 0,
          _phones: p.total_phones || (p.telnyx_number ? 1 : 0),
          _calls: filteredCalls.filter((c: any) => String(c.property_id) === String(p.uuid || p.id)).length,
          _videoCalls: filteredVideoCalls.filter((v: any) => String(v.property_id) === String(p.uuid || p.id)).length,
          _monthly: Number(p.total_monthly_revenue) || 0,
        }));

        const intercomsTotal = filteredProps.reduce((sum: number, p: any) => sum + (Number(p.total_intercoms) || 0), 0);
        const activeMobileApps = filteredProps.reduce((sum: number, p: any) => sum + (Number(p.total_active_activations) || 0), 0);

        setStats({
          integrators: integrators.length,
          customers: filteredCustomers.length,
          properties: filteredProps.length,
          units: filteredUnits.length,
          tenants: Array.isArray(filteredTenants) ? filteredTenants.length : 0,
          devices: filteredDevices.length,
          onlineDevices,
          phones,
          monthlyAmount,
          bridgeChannels: filteredBridgeChannels.length,
          onlineBridgeChannels,
          intercoms: intercomsTotal,
          activeMobileApps,
        });
        setPropertyList(enrichedProperties);
        setCustomerList(enrichedCustomers);
        const scopedOperationalEvents = [...unlockEvents, ...auditEvents].filter((event: any) => {
          const directPropertyId = event.property_id || event.property_uuid || event.metadata?.property_id || event.details?.property_id;
          if (directPropertyId && propIds.has(String(directPropertyId))) return true;
          const deviceId = event.device_id || event.device_uuid || event.metadata?.device_id || event.details?.device_id;
          return deviceId && filteredDevices.some((device: any) => String(device.uuid || device.id) === String(deviceId));
        });
        setActivityItems([...filteredCalls, ...filteredVideoCalls, ...scopedOperationalEvents].sort((a: any, b: any) => new Date(b.timestamp || b.created_at || b.occurred_at || 0).getTime() - new Date(a.timestamp || a.created_at || a.occurred_at || 0).getTime()));
      } catch (e) {
        console.error('Dashboard load error:', e);
      } finally {
        console.timeEnd('dashboard-init');
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [role, user]);

  // Recompute onlineDevices whenever live statuses arrive
  useEffect(() => {
    if (allFilteredDevices.length === 0) return;
    const onlineDevices = allFilteredDevices.filter(getDeviceOnline).length;
    setStats((prev: any) => ({ ...prev, onlineDevices }));
  }, [deviceStatuses, allFilteredDevices]);

  // Render skeleton header immediately so the page doesn't feel blocked
  const skeletonCard = (
    <div className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
      <div className="h-3 bg-gray-200 rounded w-1/2 mb-3" />
      <div className="h-7 bg-gray-200 rounded w-1/3" />
    </div>
  );

  if (loading) return (
    <div className="shadow-dashboard">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-6">
        {Array.from({ length: 8 }).map((_, i) => <React.Fragment key={i}>{skeletonCard}</React.Fragment>)}
      </div>
    </div>
  );

  const deviceStr = `${stats.onlineDevices}/${stats.devices}`;

  return <DashboardWorkspace
    user={user}
    role={role}
    stats={stats}
    properties={propertyList as any[]}
    customers={customerList as any[]}
    devices={allFilteredDevices}
    deviceStatuses={deviceStatuses as any}
    activity={activityItems}
    statusLoading={statusLoading}
  />;

  /* Previous fixed dashboard retained temporarily below while the customizable
     workspace is validated in production. */
  return (
    <div className="shadow-dashboard">
      <section className="shadow-health-band">
        <div><span>Live portfolio</span><h2>Welcome back, {user?.name || 'Intrlock user'}</h2><p>A simplified dashboard for portfolio scale and immediate exceptions.</p></div>
        <div className="shadow-health-score"><strong>{stats.devices > 0 ? Math.round((stats.onlineDevices / stats.devices) * 100) : 100}%</strong><div><p>{stats.onlineDevices} of {stats.devices} integration devices online</p><i><b style={{ width: `${stats.devices > 0 ? Math.round((stats.onlineDevices / stats.devices) * 100) : 100}%` }}/></i></div></div>
      </section>

      {/* Stats Cards */}
      <div className="shadow-stat-strip">
        {isAdmin(role) && <StatsCard title="Dealers" value={stats.integrators} icon={ICONS.integrators} to="/integrators" />}
        {(isAdmin(role) || isIntegrator(role)) && <StatsCard title={isAdmin(role) ? 'Total Customers' : 'My Customers'} value={stats.customers} icon={ICONS.customers} to="/customers" />}
        <StatsCard title={isAdmin(role) ? 'Total Properties' : 'Properties'} value={stats.properties} icon={ICONS.properties} to="/properties" />
        <StatsCard title="Units" value={stats.units} icon={ICONS.units} to="/units" />
        <StatsCard title="People" value={stats.tenants} icon={ICONS.tenants} to="/tenants" />
        {!isAdmin(role) && !isIntegrator(role) && <StatsCard title="Integration Devices" value={deviceStr} icon={ICONS.devices} change={stats.devices > 0 ? `${Math.round((stats.onlineDevices / stats.devices) * 100)}% online` : undefined} to="/devices" />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Property / Customer quick-view */}
        <div className="lg:col-span-2 space-y-6">
          {(isIntegrator(role) || isAdmin(role)) && customerList.length > 0 && (
            <>
            <section className="md:hidden dashboard-mobile-entity-section">
              <div className="dashboard-mobile-entity-heading"><h3>Recent Customers</h3><button onClick={() => navigate('/customers')}>View all</button></div>
              <div className="grid grid-cols-1 gap-4">
                {customerList.map((c: any) => {
                  const addressLine = [c.address || c.address1, c.address2, c.city, c.state, c.zip || c.zip_code || c.postal_code].filter(Boolean).join(', ');
                  const contactName = [c.contact_first_name, c.contact_last_name].filter(Boolean).join(' ') || c.contact_name;
                  return <div key={c.uuid || c.id} onClick={() => navigate(`/customers/${c.uuid || c.id}`)} className="customer-grid-card bg-white border border-gray-200 cursor-pointer hover:border-brand-400 transition-colors">
                    <div className="flex items-start justify-between mb-2 gap-2"><div className="min-w-0"><h3 className="font-semibold text-gray-900 truncate">{c.company || c.company_name || c.name}</h3></div><Badge status={c.status || 'active'}>{c.status || 'active'}</Badge></div>
                    <div className="customer-card-address"><span>Address</span><p>{addressLine || 'No address on file'}</p></div>
                    <div className="customer-card-contact"><span>Primary contact</span><strong>{contactName || 'No contact on file'}</strong>{(c.contact_email || c.email) && <a href={`mailto:${c.contact_email || c.email}`} onClick={(e) => e.stopPropagation()}>{c.contact_email || c.email}</a>}</div>
                    <details className="customer-card-metrics" onClick={(e) => e.stopPropagation()}><summary><span className="show-label">View metrics</span><span className="hide-label">Hide metrics</span><b aria-hidden="true">⌄</b></summary><div><article><span>Properties</span><strong>{c._properties || 0}</strong></article><article><span>Units</span><strong>{c._units || 0}</strong></article><article><span>Devices</span><strong>{c._devices || 0}</strong></article><article><span>People</span><strong>{c._tenants || 0}</strong></article><article><span>Phones</span><strong>{c._phones || 0}</strong></article></div></details>
                    <div className="mobile-card-actions"><button onClick={(e) => { e.stopPropagation(); navigate(`/customers/${c.uuid || c.id}`); }} className="row-open">Open <span aria-hidden="true">→</span></button></div>
                  </div>;
                })}
              </div>
            </section>
            <Card padding={false} className="hidden md:block">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Recent Customers</h3>
                <Button variant="ghost" size="sm" onClick={() => navigate('/customers')}>View All</Button>
              </div>
              <Table columns={[
                { key: 'company', header: 'Company', render: (c: any) => <span className="font-medium">{c.company || c.company_name}</span> },
                { key: '_properties', header: 'Properties' },
                { key: '_units', header: 'Units' },
                { key: '_devices', header: 'Devices' },
                { key: '_phones', header: 'Phones' },
                { key: '_tenants', header: 'Tenants' },
                { key: 'created_at', header: 'Created', render: (c: any) => c.created_at ? new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—' },
                { key: 'status', header: 'Status', render: (c: any) => <Badge status={c.status}>{c.status}</Badge> },
              ]} data={customerList} onRowClick={(c) => navigate(`/customers/${(c as any).uuid || c.id}`)} />
            </Card>
            </>
          )}
          {propertyList.length > 0 && (
            <>
            <section className="md:hidden dashboard-mobile-entity-section">
              <div className="dashboard-mobile-entity-heading"><h3>Properties</h3><button onClick={() => navigate('/properties')}>View all</button></div>
              <div className="grid grid-cols-1 gap-4">
                {propertyList.map((p: any) => {
                  const addressLine = [p.address || p.address1, p.address2, p.city, p.state, p.zip || p.zip_code || p.postal_code].filter(Boolean).join(', ');
                  return <div key={p.uuid || p.id} onClick={() => navigate(`/properties/${p.uuid || p.id}`)} className="customer-grid-card bg-white border border-gray-200 cursor-pointer hover:border-brand-400 transition-colors">
                    <div className="flex items-start justify-between mb-2 gap-2"><div className="min-w-0"><h3 className="font-semibold text-gray-900 truncate">{p.name}</h3>{p.customer_name && <p className="text-xs text-gray-500 truncate">Customer: {p.customer_name}</p>}</div><Badge status={p.status || 'active'}>{p.status || 'active'}</Badge></div>
                    <div className="customer-card-address"><span>Address</span><p>{addressLine || 'No address on file'}</p></div>
                    <div className="customer-card-contact"><span>Primary contact</span><strong>{p.contact_name || 'No contact on file'}</strong>{p.contact_email && <a href={`mailto:${p.contact_email}`} onClick={(e) => e.stopPropagation()}>{p.contact_email}</a>}</div>
                    <details className="customer-card-metrics" onClick={(e) => e.stopPropagation()}><summary><span className="show-label">View metrics</span><span className="hide-label">Hide metrics</span><b aria-hidden="true">⌄</b></summary><div><article><span>Units</span><strong>{p._units || 0}</strong></article><article><span>Devices</span><strong>{p._devices || 0}</strong></article><article><span>People</span><strong>{p._tenants || 0}</strong></article><article><span>Phones</span><strong>{p._phones || 0}</strong></article><article><span>Calls</span><strong>{p._calls || 0}</strong></article><article><span>Video</span><strong>{p._videoCalls || 0}</strong></article></div></details>
                    <div className="mobile-card-actions"><button onClick={(e) => { e.stopPropagation(); navigate(`/properties/${p.uuid || p.id}`); }} className="row-open">Open <span aria-hidden="true">→</span></button></div>
                  </div>;
                })}
              </div>
            </section>
            <Card padding={false} className="hidden md:block">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Properties</h3>
                <Button variant="ghost" size="sm" onClick={() => navigate('/properties')}>View All</Button>
              </div>
              <Table columns={[
                { key: 'name', header: 'Name', render: (p: any) => <span className="font-medium">{p.name}</span> },
                { key: 'address', header: 'Address', render: (p: any) => `${p.address || ''}, ${p.city || ''}` },
                { key: '_units', header: 'Units' },
                { key: '_devices', header: 'Devices' },
                { key: '_tenants', header: 'Tenants' },
                { key: '_phones', header: 'Phones' },
                { key: '_calls', header: 'Calls' },
                { key: '_videoCalls', header: 'Video' },
                { key: 'status', header: 'Status', render: (p: any) => <Badge status={p.status}>{p.status}</Badge> },
              ]} data={propertyList} onRowClick={(p) => navigate(`/properties/${(p as any).uuid || p.id}`)} />
            </Card>
            </>
          )}
        </div>

        {/* Quick Actions */}
        <div className="shadow-operational-signal">
            <span>Operational signal</span>
            <h3>{Math.max(0, stats.devices - stats.onlineDevices)} devices need attention</h3>
            <p>The live portfolio currently reports {stats.onlineDevices} of {stats.devices} integration devices online.</p>
            <button onClick={() => navigate('/devices')}>Review devices <span>→</span></button>
            <details><summary>Quick actions</summary><div className="space-y-2">
              {isAdmin(role) && (
                <Button variant="secondary" className="w-full justify-start" onClick={() => navigate('/integrators')}>
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                  Add Dealer
                </Button>
              )}
              {(isAdmin(role) || isIntegrator(role)) && (
                <Button variant="secondary" className="w-full justify-start" onClick={() => navigate('/customers')}>
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                  Add Customer
                </Button>
              )}
              {(isAdmin(role) || isIntegrator(role) || isCustomer(role)) && (
                <Button variant="secondary" className="w-full justify-start" onClick={() => navigate('/properties')}>
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                  Add Property
                </Button>
              )}
              {(isAdmin(role) || isIntegrator(role) || isCustomer(role)) && (
                <Button variant="secondary" className="w-full justify-start" onClick={() => navigate('/devices')}>
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                  Add Device
                </Button>
              )}
            </div></details>
        </div>
      </div>
    </div>
  );
};
